import React, { useState, useEffect, useRef } from 'react';
import { getPracticeOverviewSummary } from 'api/group';
import { TPracticeOverviewSummaryVo, TPracticeOverviewRange } from '../../types';
import RangeDropdown from '../RangeDropdown';
import s from './s.module.less';

type Props = {
    onSummaryChange?: (summary: TPracticeOverviewSummaryVo | null) => void;
};

const DEFAULT_RANGE: TPracticeOverviewRange = 'LAST_30D';

const ArrowUp = () => (
    <svg viewBox="0 0 20 20" fill="none" stroke="#065F46" strokeWidth="2" strokeLinecap="round" width="20" height="20">
        <line x1="10" y1="14" x2="10" y2="6" />
        <polyline points="7 9 10 6 13 9" />
    </svg>
);

const ArrowDown = () => (
    <svg viewBox="0 0 20 20" fill="none" stroke="#991B1B" strokeWidth="2" strokeLinecap="round" width="20" height="20">
        <line x1="10" y1="6" x2="10" y2="14" />
        <polyline points="7 11 10 14 13 11" />
    </svg>
);

const formatCents = (cents: number): string => {
    const dollars = cents / 100;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
    return `$${dollars.toLocaleString()}`;
};

const formatPct = (val: number | null | undefined): string => {
    if (val == null) return '0%';
    return `${Math.abs(val).toFixed(1)}%`;
};

type CardConfig = {
    label: string;
    getValue: (d: TPracticeOverviewSummaryVo) => string;
    getPrev: (d: TPracticeOverviewSummaryVo) => string;
    getChangePct: (d: TPracticeOverviewSummaryVo) => string;
    getDirection: (d: TPracticeOverviewSummaryVo) => 'up' | 'down';
};

const CARDS: CardConfig[] = [
    {
        label: 'Revenue',
        getValue: (d) => formatCents(d.payoutCents),
        getPrev: (d) => `from ${formatCents(d.payoutCentsPrior)}`,
        getChangePct: (d) => formatPct(d.payoutChangePct),
        getDirection: (d) => (d.payoutChangePct >= 0 ? 'up' : 'down'),
    },
    // {
    //     label: 'Completed appointments',
    //     getValue: (d) => String(d.scheduled),
    //     getPrev: (d) => `from ${d.scheduledPrior}`,
    //     getChangePct: (d) => formatPct(d.scheduledChangePct),
    //     getDirection: (d) => (d.scheduledChangePct >= 0 ? 'up' : 'down'),
    // },
    {
        label: 'New patients',
        getValue: (d) => String(d.newPatients),
        getPrev: (d) => `from ${d.newPatientsPrior}`,
        getChangePct: (d) => formatPct(d.newPatientsChangePct),
        getDirection: (d) => (d.newPatientsChangePct >= 0 ? 'up' : 'down'),
    },
    {
        label: 'Avg. patient rating',
        getValue: (d) => d.avgRating?.toFixed(1) ?? '-',
        getPrev: (d) => `${d.ratingCount} reviews`,
        getChangePct: (d) => (d.avgRatingPriorDelta != null ? Math.abs(d.avgRatingPriorDelta).toFixed(1) : '0'),
        getDirection: (d) => ((d.avgRatingPriorDelta ?? 0) >= 0 ? 'up' : 'down'),
    },
];

const PerformanceOverview: React.FC<Props> = ({ onSummaryChange }) => {
    const [range, setRange] = useState<TPracticeOverviewRange>(DEFAULT_RANGE);
    const [data, setData] = useState<TPracticeOverviewSummaryVo | null>(null);
    const onSummaryChangeRef = useRef(onSummaryChange);
    onSummaryChangeRef.current = onSummaryChange;

    useEffect(() => {
        getPracticeOverviewSummary({ range }).then((res) => {
            const summary = !res.error ? (res?.data?.data ?? null) : null;
            setData(summary);
            onSummaryChangeRef.current?.(summary);
        });
    }, [range]);

    if (!data) return null;

    return (
        <div>
            <div className={s.header}>
                <h2 className={s.title}>Performance Overview</h2>
                <RangeDropdown value={range} onChange={(v) => setRange(v as TPracticeOverviewRange)} />
            </div>
            <div className={s.grid}>
                {CARDS.map((card, i) => {
                    const direction = card.getDirection(data);
                    return (
                        <div key={i} className={s.card}>
                            <div className={s.label}>{card.label}</div>
                            <div className={s.valRow}>
                                <span className={s.val}>{card.getValue(data)}</span>
                                <span className={s.from}>{card.getPrev(data)}</span>
                            </div>
                            <div className={s.trend}>
                                <span className={`${s.badge} ${direction === 'up' ? s.up : s.down}`}>
                                    {direction === 'up' ? <ArrowUp /> : <ArrowDown />}
                                    <span className={`${s.pct} ${direction === 'up' ? s.pctUp : s.pctDown}`}>
                                        {card.getChangePct(data)}
                                    </span>
                                </span>
                                <span className={s.period}>from last period</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PerformanceOverview;
